import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Filter, Loader2 } from "lucide-react";
import {
  useScheduleData,
  type UseScheduleDataReturn,
} from "@/components/Scheduler/scheduleData";

export type TimeSlot =
  | { time: string; status: "available"; availablePractitioners: string[] }
  | {
      time: string;
      status: "booked";
      patient: string;
      service: string;
      practitioner: string;
      room?: string;
    };

interface SelectedSlot {
  time: string;
  date: Date;
  service?: string;
  serviceId?: string;
  practitioner?: string;
  practitionerId?: string;
  room?: string;
}

interface AppointmentSlotPickerProps {
  isDarkMode: boolean;
  onSlotSelected: (slot: SelectedSlot) => void;
  onBack?: () => void;
  initialDate?: Date;
  externalTimeSlots?: TimeSlot[];
  travelDepartureDate?: string;
  travelReturnDate?: string;
}

const getInitials = (name: string): string => {
  const parts = name.split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getPractitionerColor = (name: string): string => {
  const colors = [
    "bg-blue-500",
    "bg-purple-500",
    "bg-green-500",
    "bg-orange-500",
    "bg-pink-500",
    "bg-teal-500",
    "bg-indigo-500",
    "bg-red-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

function formatDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isDateBeforeToday(d: Date): boolean {
  return startOfDay(d).getTime() < startOfDay(new Date()).getTime();
}

function isDateWithinTravelPeriod(
  d: Date,
  departureDateIso?: string,
  returnDateIso?: string,
): boolean {
  if (!departureDateIso || !returnDateIso) return false;
  const day = startOfDay(d).getTime();
  const dep = startOfDay(new Date(departureDateIso + "T00:00:00")).getTime();
  const ret = startOfDay(new Date(returnDateIso + "T00:00:00")).getTime();
  return day > dep && day < ret;
}

function isTimeSlotInPast(slotDate: Date, time: string): boolean {
  const slotDay = startOfDay(slotDate);
  const today = startOfDay(new Date());
  if (slotDay.getTime() < today.getTime()) return true;
  if (slotDay.getTime() > today.getTime()) return false;

  const [hours, minutes] = time.split(":").map(Number);
  const slotDateTime = new Date(slotDate);
  slotDateTime.setHours(hours || 0, minutes || 0, 0, 0);
  return slotDateTime.getTime() < Date.now();
}

export function AppointmentSlotPicker({
  isDarkMode,
  onSlotSelected,
  onBack,
  initialDate,
  externalTimeSlots,
  travelDepartureDate,
  travelReturnDate,
}: AppointmentSlotPickerProps) {
  const [date, setDate] = useState(initialDate || new Date());
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedPharmacistId, setSelectedPharmacistId] = useState("");
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    if (initialDate) {
      setDate(initialDate);
    }
  }, [initialDate?.getTime()]);

  const s = useScheduleData();
  const {
    servicePharmacists = [],
    serviceTypesInfo = [],
    availableDates = [],
    getAvailableTimesForDate,
    getFilteredBookingsForDate,
    onServiceTypeSelected,
    onPharmacistSelected,
  } = (s || {}) as Partial<UseScheduleDataReturn>;

  const dateKey = formatDateKey(date);

  const handleServiceChange = useCallback(
    async (serviceId: string) => {
      setSelectedServiceId(serviceId);
      setSelectedPharmacistId("");
      setDateError("");
      if (onServiceTypeSelected) {
        await onServiceTypeSelected(serviceId);
      }
    },
    [onServiceTypeSelected],
  );

  const handlePharmacistChange = useCallback(
    async (pharmacistId: string) => {
      setSelectedPharmacistId(pharmacistId);
      setDateError("");
      if (onPharmacistSelected && selectedServiceId) {
        await onPharmacistSelected(selectedServiceId, pharmacistId);
      }
    },
    [onPharmacistSelected, selectedServiceId],
  );

  const changeDate = useCallback(
    (newDate: Date) => {
      if (isDateBeforeToday(newDate)) {
        setDateError("Appointments cannot be booked in the past.");
        return;
      }
      setDateError("");
      setDate(newDate);
    },
    [],
  );

  const isAtEarliestDate = useMemo(() => isDateBeforeToday(new Date(date.getTime() - 86400000)) || startOfDay(date).getTime() === startOfDay(new Date()).getTime(), [date]);

  const prevDay = useCallback(() => {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    if (isDateBeforeToday(d)) return;
    changeDate(d);
  }, [date, changeDate]);

  const nextDay = useCallback(() => {
    const d = new Date(date);
    d.setDate(d.getDate() + 1);
    changeDate(d);
  }, [date, changeDate]);

  const resetToday = useCallback(() => changeDate(new Date()), [changeDate]);

  const jumpToDate = useCallback(
    (daysOrMonths: number, unit: "days" | "months") => {
      const target = new Date();
      if (unit === "days") {
        target.setDate(target.getDate() + daysOrMonths);
      } else {
        target.setMonth(target.getMonth() + daysOrMonths);
      }
      changeDate(target);
    },
    [changeDate],
  );

  useEffect(() => {
    if (isDateWithinTravelPeriod(date, travelDepartureDate, travelReturnDate)) {
      setDateError(
        "The patient is traveling between the departure and return dates — appointments cannot be booked on this date.",
      );
    } else {
      setDateError("");
    }
  }, [date, travelDepartureDate, travelReturnDate]);

  const availableTimesForDate = useMemo(() => {
    if (!selectedPharmacistId || !getAvailableTimesForDate) return [];
    return getAvailableTimesForDate(dateKey);
  }, [dateKey, selectedPharmacistId, getAvailableTimesForDate]);

  const bookingsForDate = useMemo(() => {
    if (!getFilteredBookingsForDate) return [];
    return getFilteredBookingsForDate(dateKey);
  }, [dateKey, getFilteredBookingsForDate]);

  const timeSlots: TimeSlot[] = useMemo(() => {
    if (externalTimeSlots && externalTimeSlots.length > 0) {
      return externalTimeSlots;
    }

    const slots: TimeSlot[] = [];
    const seenTimes = new Set<string>();

    for (const time of availableTimesForDate) {
      seenTimes.add(time);
      slots.push({
        time,
        status: "available",
        availablePractitioners: selectedPharmacistId
          ? [
              servicePharmacists.find(
                (p: any) => p.id === selectedPharmacistId,
              )?.name || selectedPharmacistId,
            ]
          : [],
      });
    }

    for (const booking of bookingsForDate) {
      const bookingTime =
        booking.time ||
        (booking.startDate
          ? new Date(booking.startDate).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "");
      if (bookingTime && !seenTimes.has(bookingTime)) {
        seenTimes.add(bookingTime);
        slots.push({
          time: bookingTime,
          status: "booked",
          patient: booking.patientName || booking.patient || "Booked",
          service: booking.serviceType || booking.service || "",
          practitioner: booking.pharmacist || booking.practitioner || "",
          room: booking.room || "",
        });
      }
    }

    slots.sort((a, b) => a.time.localeCompare(b.time));
    return slots;
  }, [
    externalTimeSlots,
    availableTimesForDate,
    bookingsForDate,
    selectedPharmacistId,
    servicePharmacists,
  ]);

  const availableCount = useMemo(
    () => timeSlots.filter((s) => s.status === "available").length,
    [timeSlots],
  );
  const bookedCount = useMemo(
    () => timeSlots.filter((s) => s.status === "booked").length,
    [timeSlots],
  );

  const isTravelBlockedDate = isDateWithinTravelPeriod(
    date,
    travelDepartureDate,
    travelReturnDate,
  );

  const handleSlotClick = (slot: TimeSlot) => {
    if (isTravelBlockedDate) {
      return;
    }
    if (isTimeSlotInPast(date, slot.time)) {
      setDateError("This time has already passed and cannot be booked.");
      return;
    }
    if (slot.status === "available") {
      const pharmacistName = selectedPharmacistId
        ? servicePharmacists.find((p: any) => p.id === selectedPharmacistId)
            ?.name || ""
        : "";
      const serviceName = selectedServiceId
        ? (serviceTypesInfo || []).find(
            (s: any) => s._id === selectedServiceId,
          )?.name || ""
        : "";
      onSlotSelected({
        time: slot.time,
        date,
        service: serviceName,
        serviceId: selectedServiceId || undefined,
        practitioner: pharmacistName,
        practitionerId: selectedPharmacistId || undefined,
      });
    } else {
      onSlotSelected({
        time: slot.time,
        date,
        service: slot.service,
        practitioner: slot.practitioner,
        room: slot.room,
      });
    }
  };

  const needsServiceSelection = !selectedServiceId && !externalTimeSlots;
  const needsPharmacistSelection =
    selectedServiceId && !selectedPharmacistId && !externalTimeSlots;

  return (
    <div className="px-6 pb-6 pt-2">
      <div className="mb-4">
        <p
          className={`text-sm transition-colors duration-300 ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
        >
          Choose an available time slot to book your appointment
        </p>
      </div>

      {/* Service & Pharmacist Cascade */}
      {!externalTimeSlots && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* Step 1: Service */}
          <div className="space-y-1">
            <span
              className={`text-xs font-medium ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Service *
            </span>
            <Select
              value={selectedServiceId}
              onValueChange={handleServiceChange}
            >
              <SelectTrigger
                className={`h-8 text-sm ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700 text-gray-200"
                    : "border-gray-300 bg-white text-gray-700"
                }`}
              >
                <SelectValue placeholder="Select service" />
              </SelectTrigger>
              <SelectContent
                className={isDarkMode ? "bg-gray-800 border-gray-700" : ""}
              >
                {(serviceTypesInfo || [])
                  .filter(
                    (s: any) => s.serviceType !== 1 && s.serviceType !== 3,
                  )
                  .map((s: any) => (
                    <SelectItem key={s._id} value={s._id}>
                      {s.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Step 2: Pharmacist (enabled after service) */}
          <div className="space-y-1">
            <span
              className={`text-xs font-medium ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Pharmacist *
            </span>
            <Select
              value={selectedPharmacistId}
              onValueChange={handlePharmacistChange}
              disabled={!selectedServiceId}
            >
              <SelectTrigger
                className={`h-8 text-sm ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700 text-gray-200"
                    : "border-gray-300 bg-white text-gray-700"
                } ${!selectedServiceId ? "opacity-50" : ""}`}
              >
                <SelectValue
                  placeholder={
                    selectedServiceId
                      ? "Select pharmacist"
                      : "Select service first"
                  }
                />
              </SelectTrigger>
              <SelectContent
                className={isDarkMode ? "bg-gray-800 border-gray-700" : ""}
              >
                {servicePharmacists.map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Date error */}
      {dateError && (
        <div
          className={`mb-3 px-3 py-2 rounded-md text-xs font-medium ${
            isDarkMode
              ? "bg-red-900/30 border border-red-700 text-red-300"
              : "bg-red-50 border border-red-200 text-red-700"
          }`}
        >
          {dateError}
        </div>
      )}

      {/* Date Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevDay}
          disabled={isAtEarliestDate}
          className={`p-2 rounded-md transition-colors duration-300 ${
            isAtEarliestDate
              ? "opacity-40 cursor-not-allowed"
              : isDarkMode
                ? "text-gray-400 hover:text-white hover:bg-gray-700"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex flex-col items-center">
          <div
            className={`text-sm font-medium transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {date.toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
          </div>
          <div
            className={`text-xs transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {date.getFullYear()}
            {date.toDateString() === new Date().toDateString() && (
              <span
                className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                  isDarkMode
                    ? "bg-blue-900 text-blue-200"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                Today
              </span>
            )}
            {selectedPharmacistId &&
              availableDates &&
              availableDates.includes(dateKey) && (
                <span
                  className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                    isDarkMode
                      ? "bg-green-900 text-green-200"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  Available
                </span>
              )}
          </div>
        </div>

        <button
          onClick={nextDay}
          className={`p-2 rounded-md transition-colors duration-300 ${
            isDarkMode
              ? "text-gray-400 hover:text-white hover:bg-gray-700"
              : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          }`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Quick Date Navigation */}
      <div className="mb-4">
        <div
          className={`text-xs font-medium mb-2 transition-colors duration-300 ${
            isDarkMode ? "text-gray-300" : "text-gray-600"
          }`}
        >
          Quick jump to:
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: "+7 days", value: 7, unit: "days" as const },
            { label: "+14 days", value: 14, unit: "days" as const },
            { label: "+28 days", value: 28, unit: "days" as const },
            { label: "+2 months", value: 2, unit: "months" as const },
            { label: "+6 months", value: 6, unit: "months" as const },
            { label: "+12 months", value: 12, unit: "months" as const },
          ].map((j) => (
            <button
              key={j.label}
              onClick={() => jumpToDate(j.value, j.unit)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors duration-200 ${
                isDarkMode
                  ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 hover:border-gray-500"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300"
              }`}
            >
              {j.label}
            </button>
          ))}
        </div>
      </div>

      {/* Prompt to select service/pharmacist */}
      {needsServiceSelection && (
        <div
          className={`text-center py-8 text-sm ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        >
          Select a service to get started
        </div>
      )}

      {needsPharmacistSelection && (
        <div
          className={`text-center py-8 text-sm ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        >
          Select a pharmacist to see available slots
        </div>
      )}

      {/* Time Slots Grid */}
      {!needsServiceSelection && !needsPharmacistSelection && (
        <>
          {timeSlots.length === 0 && !dateError ? (
            <div
              className={`text-center py-8 text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              No available slots for this date
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 mb-4 max-h-[400px] overflow-y-auto scrollbar-thin">
              {timeSlots.map((slot) => {
                const isPastSlot = isTimeSlotInPast(date, slot.time);
                const isDisabledSlot = isPastSlot || isTravelBlockedDate;
                return (
                <div
                  key={slot.time}
                  onClick={() => !isDisabledSlot && handleSlotClick(slot)}
                  className={`p-3 rounded-md border transition-all duration-200 ${
                    isDisabledSlot
                      ? "cursor-not-allowed opacity-40"
                      : "cursor-pointer"
                  } ${
                    slot.status === "booked"
                      ? isDarkMode
                        ? "border-blue-600 bg-blue-900/30 hover:bg-blue-900/50"
                        : "border-blue-300 bg-blue-50 hover:bg-blue-100"
                      : isDarkMode
                        ? "border-gray-600 bg-gray-700 hover:bg-gray-600 hover:border-gray-500"
                        : "border-gray-200 bg-gray-50 hover:bg-white hover:border-gray-300"
                  }`}
                >
                  <div
                    className={`text-sm font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {slot.time}
                  </div>
                  {slot.status === "available" ? (
                    <div className="space-y-2">
                      <div
                        className={`text-xs transition-colors duration-300 ${
                          isDarkMode ? "text-green-400" : "text-green-600"
                        }`}
                      >
                        Available
                      </div>
                      {slot.availablePractitioners.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {slot.availablePractitioners.map((practitioner) => (
                            <div
                              key={practitioner}
                              className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-semibold ${getPractitionerColor(practitioner)}`}
                              title={practitioner}
                            >
                              {getInitials(practitioner)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div
                        className={`text-xs font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-blue-300" : "text-blue-700"
                        }`}
                      >
                        {slot.patient}
                      </div>
                      <div
                        className={`text-xs transition-colors duration-300 ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      >
                        {slot.service}
                      </div>
                      {slot.room && (
                        <div
                          className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? "text-blue-400" : "text-blue-500"
                          }`}
                        >
                          {slot.room}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Footer */}
      <div
        className={`flex items-center justify-between pt-3 border-t transition-colors duration-300 ${
          isDarkMode ? "border-gray-600" : "border-gray-200"
        }`}
      >
        <div className="flex items-center gap-2">
          {date.toDateString() !== new Date().toDateString() && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetToday}
              className={`transition-colors duration-300 ${
                isDarkMode
                  ? "text-gray-400 hover:text-white hover:bg-gray-700"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              Today
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full transition-colors duration-300 ${
              isDarkMode ? "bg-green-500" : "bg-green-400"
            }`}
          ></div>
          <span
            className={`text-xs transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {availableCount} available, {bookedCount} booked
          </span>
        </div>
      </div>
    </div>
  );
}
